import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import AuthLayout from "../../shared/ui/AuthLayout";
import AuthSectionHeader from "../../shared/ui/AuthSectionHeader";
import Button from "../../shared/ui/Button";
import Input from "../../shared/ui/Input";
import { useTranslation } from "../../i18n";
import api from "../../utils/api";
import { getGuestReturnPath, isAuthenticated } from "../../lib/authRoutes";

const MailIcon = () => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className="text-[#111111]"
  >
    <path
      d="M4 7L12 13L20 7"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <rect
      x="3"
      y="5"
      width="18"
      height="14"
      rx="2"
      stroke="currentColor"
      strokeWidth="1.8"
    />
  </svg>
);

const ForgotPasswordForm = () => {
  const { t } = useTranslation();
  // Reached from Settings → change password (logged in) or the login page
  // (logged out); the "back" control follows the user's actual context.
  const returnPath = getGuestReturnPath();
  const backLabel = isAuthenticated()
    ? t("common.backToDashboard")
    : t("auth.backToLogin");
  const [formData, setFormData] = useState({
    email: "",
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const timeoutRef = useRef(null);

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    setErrors((prev) => ({
      ...prev,
      [name]: "",
    }));
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.email.trim()) {
      newErrors.email = t("auth.emailRequired");
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = t("auth.emailInvalid");
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (validateForm()) {
      setIsSubmitting(true);
      try {
        // The endpoint always returns 200 (no account enumeration), so we
        // show the confirmation regardless of whether the address exists.
        await api.post("/api/users/password-reset/", {
          email: formData.email.trim(),
        });
      } catch {
        /* still show the neutral confirmation below */
      } finally {
        setIsSubmitting(false);
        setIsSubmitted(true);
      }
    }
  };

  useEffect(() => {
    return () => {
      if (timeoutRef.current) window.clearTimeout(timeoutRef.current);
    };
  }, []);

  const InlineSpinner = () => (
    <svg
      className="h-4 w-4 animate-spin text-white"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden
    >
      <circle
        cx="12"
        cy="12"
        r="10"
        stroke="rgba(255,255,255,0.35)"
        strokeWidth="3"
      />
      <path
        d="M22 12a10 10 0 0 0-10-10"
        stroke="currentColor"
        strokeWidth="3"
        strokeLinecap="round"
      />
    </svg>
  );

  return (
    <AuthLayout consentText={t("auth.consentPrefix")}>
      {!isSubmitted ? (
        <form onSubmit={handleSubmit}>
          <AuthSectionHeader
            title={t("auth.forgotPasswordTitle")}
            description={t("auth.forgotPasswordSubtitle")}
          />

          <div className="space-y-6">
            <Input
              type="email"
              name="email"
              label={t("auth.email")}
              value={formData.email}
              onChange={handleChange}
              placeholder="max@unternehmen.de"
              error={errors.email}
            />

            <Button
              type="submit"
              showArrow={!isSubmitting}
              className="mt-2"
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <span className="inline-flex items-center gap-2">
                  <InlineSpinner />
                  <span>{t("auth.resetLinkSending")}</span>
                </span>
              ) : (
                t("auth.resetLinkSent")
              )}
            </Button>
          </div>

          <div className="my-8 h-px w-full bg-[#ececec]" />

          <div className="pt-1 text-center">
            <Link
              to={returnPath}
              className="inline-flex items-center justify-center gap-2 text-[14px] font-medium text-[#4b5563] transition hover:text-black"
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M15 18L9 12L15 6"
                  stroke="currentColor"
                  strokeWidth="1.6"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              <span>{backLabel}</span>
            </Link>
          </div>
        </form>
      ) : (
        <div>
          <div className="mb-8 text-center">
            <div className="mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-full bg-[#eef2f6]">
              <MailIcon />
            </div>

            <AuthSectionHeader
              title={t("auth.emailSentTitle")}
              description={t("auth.emailSentBody")}
              className="mb-0"
            />
          </div>

          <div className="space-y-4">
            <Link to={returnPath} className="block">
              <Button type="button" variant="ghost">
                {backLabel}
              </Button>
            </Link>
          </div>
        </div>
      )}
    </AuthLayout>
  );
};

export default ForgotPasswordForm;
